import { parse } from 'query-string'

import { Origin } from '../graphql'

interface PhoenixUrlParams {
  origin: Origin
  bookableItemId?: string
  productId?: string
  cartId?: string
  orderId?: string
  venueId?: string
  paymentInProgress?: boolean
  lastPaymentAttempt?: Date
  courtId?: string
  sportId?: string
  trainerId?: string
  privateSessionOfferId?: string
  start?: string
  end?: string
  amount?: number
  imageId?: string
  message?: string
  skipConfirmation?: boolean
  isSetupFlow?: boolean
  redirectToBrandedAppUri?: string // URI of the branded app to redirect back to after the payment
  isReturningFromStripe?: boolean // Indicates that the page is loaded after returning from Stripe
  hideEversportsBranding?: boolean // Hide Eversports branding (alternative to feature flag)
}

const mapStringToOrigin = (search: string): Origin => {
  const { origin } = parse(search)

  if (!origin || typeof origin !== 'string') return Origin.ORIGIN_MARKETPLACE

  switch (origin.toLowerCase()) {
    case 'widget':
      return Origin.ORIGIN_WIDGET
    case 'provider-api':
      return Origin.ORIGIN_PROVIDER_API
    default:
      return Origin.ORIGIN_MARKETPLACE
  }
}

const getPhoenixUrlParams = (search: string): PhoenixUrlParams => {
  const query = parse(search)

  const origin = mapStringToOrigin(search)
  const {
    bookableItemId,
    courtId,
    productId,
    cartId,
    orderId,
    sportId,
    venueId,
    trainerId,
    privateSessionOfferId,
    start,
    end,
    amount,
    imageId,
    message,
    paymentInProgress,
    lastPaymentAttempt,
    skipConfirmation,
    isSetupFlow,
    redirectToBrandedAppUri,
    isReturningFromStripe,
    hideEversportsBranding,
  } = query

  const params: PhoenixUrlParams = { origin }

  if (bookableItemId) {
    params.bookableItemId = String(bookableItemId)
  }

  if (courtId) {
    params.courtId = String(courtId)
  }

  if (productId) {
    params.productId = String(productId)
  }

  if (cartId) {
    params.cartId = String(cartId)
  }

  if (orderId) {
    params.orderId = String(orderId)
  }

  if (sportId) {
    params.sportId = String(orderId)
  }

  if (venueId) {
    params.venueId = String(venueId)
  }

  if (trainerId) {
    params.trainerId = String(trainerId)
  }

  if (privateSessionOfferId) {
    params.privateSessionOfferId = String(privateSessionOfferId)
  }

  if (start) {
    params.start = String(start)
  }

  if (end) {
    params.end = String(end)
  }

  if (amount) {
    params.amount = Number(amount)
  }

  if (imageId) {
    params.imageId = String(imageId)
  }

  if (message) {
    params.message = String(message)
  }

  if (paymentInProgress) {
    params.paymentInProgress = Boolean(paymentInProgress)
  }

  if (lastPaymentAttempt) {
    params.lastPaymentAttempt = new Date(Number(lastPaymentAttempt))
  }

  if (skipConfirmation) {
    params.skipConfirmation = Boolean(skipConfirmation)
  }

  if (isSetupFlow) {
    params.isSetupFlow = Boolean(isSetupFlow)
  }

  if (redirectToBrandedAppUri) {
    params.redirectToBrandedAppUri = String(redirectToBrandedAppUri)
  }

  if (isReturningFromStripe) {
    params.isReturningFromStripe = Boolean(isReturningFromStripe)
  }

  if (hideEversportsBranding) {
    params.hideEversportsBranding = Boolean(hideEversportsBranding)
  }

  return params
}

export const clearPaymentUrlParams = (search: string): string => {
  const urlParams = new URLSearchParams(search)
  urlParams.delete('paymentInProgress')
  urlParams.delete('isSetupFlow')
  urlParams.delete('lastPaymentAttempt')
  urlParams.delete('isReturningFromStripe')
  return urlParams.toString()
}

export default getPhoenixUrlParams
